import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { useCompanies } from '../../features/company/CompanyContext';
import { Card, Loading, ModuleHero, Notice, PersonCard, Spinner, StatusPill } from '../../components/ui';
import Select, { type SelectOption } from '../../components/Select';
import GlossaryHint from '../../components/GlossaryHint';
import YeabeAddressPicker, { type PickedAddress } from '../../components/YeabeAddressPicker';
import { errorMessage } from '../../lib/http';
import { COMPANY_STATUS, TRACK_TONE, flagEmoji } from '../../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, FIELD, FIELD_STYLE, LABEL } from '../../lib/ui';
import type { Company, CompanyTypeView, RatingTrack, RatingTrackView } from '../../types/workspace';
import CompanyLogo from '../../components/CompanyLogo';

type Overview = { company: Company | null; countries?: { code: string; name: string }[] };

const EMPTY = {
  logicalName: '',
  name: '',
  legalName: '',
  countryCode: 'IN',
  companyType: '',
  workspaceLabel: '',
  targetLender: '',
  sector: '',
  registrationNumber: '',
  website: '',
  email: '',
  phone: '',
  addressLine: '',
  addressLine2: '',
  city: '',
  stateCode: '',
  stateName: '',
  postalCode: '',
  addressSource: '' as '' | 'YEABE' | 'MANUAL',
  yeabeAddressId: '',
  foundedYear: '',
  employeeCount: '',
  annualRevenue: '',
  description: '',
  fundingAmount: '',
  fundingPurpose: '',
  fundingCurrency: '',
  fundingInstrument: '',
  fundingTenureMonths: '',
  fundingRepaymentPlan: '',
  fundingSecurity: '',
};

type Form = typeof EMPTY;

const ADDRESS_KEYS: (keyof Form)[] = ['addressLine', 'addressLine2', 'city', 'stateCode', 'stateName', 'postalCode'];

const SECTORS: SelectOption[] = [
  'Manufacturing',
  'Logistics',
  'Retail & e-commerce',
  'Technology / SaaS',
  'Fintech',
  'Healthcare',
  'Energy & cleantech',
  'Agriculture',
  'Real estate',
  'Construction & infrastructure',
  'Hospitality',
  'Education',
  'Services',
  'Other',
].map((s) => ({ value: s, label: s }));

const REG_HINT: Record<string, string> = {
  IN: 'CIN / LLPIN / Udyam number',
  US: 'EIN / state file number',
  GB: 'Companies House number',
  SG: 'UEN',
  AE: 'Trade licence number',
};

function toForm(c: Company): Form {
  const s = (v: string | null | undefined) => v || '';
  return {
    logicalName: s(c.logicalName),
    name: s(c.name),
    legalName: s(c.legalName),
    countryCode: c.countryCode,
    companyType: s(c.companyType),
    workspaceLabel: s(c.workspaceLabel),
    targetLender: s(c.targetLender),
    sector: s(c.sector),
    registrationNumber: s(c.registrationNumber),
    website: s(c.website),
    email: s(c.email),
    phone: s(c.phone),
    addressLine: s(c.addressLine),
    addressLine2: s(c.addressLine2),
    city: s(c.city),
    stateCode: s(c.stateCode),
    stateName: s(c.stateName),
    postalCode: s(c.postalCode),
    addressSource: c.addressSource || '',
    yeabeAddressId: s(c.yeabeAddressId),
    foundedYear: c.foundedYear ? String(c.foundedYear) : '',
    employeeCount: c.employeeCount != null ? String(c.employeeCount) : '',
    annualRevenue: s(c.annualRevenue),
    description: s(c.description),
    fundingAmount: s(c.fundingAmount),
    fundingPurpose: s(c.fundingPurpose),
    fundingCurrency: s(c.fundingCurrency),
    fundingInstrument: s(c.fundingInstrument),
    fundingTenureMonths: c.fundingTenureMonths != null ? String(c.fundingTenureMonths) : '',
    fundingRepaymentPlan: s(c.fundingRepaymentPlan),
    fundingSecurity: s(c.fundingSecurity),
  };
}

export default function CompanyProfile({ mode = 'edit' }: { mode?: 'edit' | 'new' }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { companies, select, refresh } = useCompanies();
  const isNewMode = mode === 'new';
  const overview = useApi<Overview>(isNewMode ? null : '/customer/overview');
  const countriesApi = useApi<{ countries: { code: string; name: string }[] }>('/customer/countries');
  const [form, setForm] = useState<Form>(EMPTY);
  const [types, setTypes] = useState<CompanyTypeView[]>([]);
  const [tracks, setTracks] = useState<RatingTrackView[]>([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [typeTouched, setTypeTouched] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const company = isNewMode ? null : overview.data?.company || null;
  const locked = Boolean(company && !['INVITED', 'INTAKE'].includes(company.status));
  const sourceId = params.get('from');
  const source = isNewMode && sourceId ? companies.find((c) => c.id === sourceId) || null : null;

  useEffect(() => {
    if (company) setForm(toForm(company));
  }, [company]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  useEffect(() => {
    if (!source) return;
    setForm({ ...toForm(source), workspaceLabel: '', targetLender: '', fundingAmount: '', fundingPurpose: '' });
  }, [source]);

  useEffect(() => {
    void api
      .get('/customer/company-types', { params: { country: form.countryCode } })
      .then(({ data }) => {
        setTypes(data.types || []);
        setTracks(data.tracks || []);
      })
      .catch(() => setTypes([]));
  }, [form.countryCode]);

  const selectedType = types.find((t) => t.code === form.companyType) || null;
  const track = selectedType ? tracks.find((t) => t.code === selectedType.track) || null : null;
  const typeMissing = !selectedType;

  /** Mirrors the server's funding rules so customers see which field to fix before saving. */
  const validateFunding = (f: Form): Partial<Record<keyof Form, string>> => {
    const errors: Partial<Record<keyof Form, string>> = {};
    const amount = f.fundingAmount.trim();
    if (amount && !/\d/.test(amount)) errors.fundingAmount = 'Funding ask must include a number, e.g. 1500000 or 1.5M.';
    if (amount) {
      const currency = f.fundingCurrency.trim();
      if (!currency) errors.fundingCurrency = 'Currency is required when a funding amount is entered.';
      else if (!/^[A-Za-z]{3}$/.test(currency)) errors.fundingCurrency = 'Use a 3-letter currency code, e.g. INR or USD.';
      if (!f.fundingInstrument) errors.fundingInstrument = 'Funding instrument is required when a funding amount is entered.';
    }
    if (f.fundingTenureMonths) {
      const tenure = Number(f.fundingTenureMonths);
      if (!Number.isFinite(tenure) || tenure < 1 || tenure > 600) errors.fundingTenureMonths = 'Tenure must be between 1 and 600 months.';
    }
    if (f.fundingInstrument === 'DEBT' && amount && !f.fundingTenureMonths) errors.fundingTenureMonths = 'Tenure in months is required for debt facilities.';
    if (f.fundingInstrument === 'DEBT' && amount && !f.fundingRepaymentPlan.trim()) errors.fundingRepaymentPlan = 'Repayment plan is required for debt facilities.';
    return errors;
  };
  const fieldA11y = (k: keyof Form) => (fieldErrors[k] ? { 'aria-invalid': true as const, 'aria-describedby': `cp-${k}-error` } : {});
  const fieldError = (k: keyof Form) =>
    fieldErrors[k] ? (
      <p id={`cp-${k}-error`} role="alert" className="mt-1 text-xs font-semibold text-[#e11d48]">
        {fieldErrors[k]}
      </p>
    ) : null;

  const setValue = <K extends keyof Form>(k: K, v: Form[K]) => {
    setFieldErrors((errs) => {
      if (k === 'fundingInstrument') return {};
      if (!errs[k]) return errs;
      const { [k]: _cleared, ...rest } = errs;
      return rest;
    });
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (ADDRESS_KEYS.includes(k) && f.addressSource === 'YEABE') {
        next.addressSource = 'MANUAL';
        next.yeabeAddressId = '';
      }
      if (k === 'countryCode' && v !== f.countryCode) next.companyType = '';
      return next;
    });
  };
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setValue(k, e.target.value as never);

  const onPickAddress = (a: PickedAddress) => {
    setForm((f) => ({
      ...f,
      addressLine: a.addressLine,
      addressLine2: a.addressLine2,
      city: a.city,
      stateCode: a.stateCode,
      stateName: a.stateName,
      postalCode: a.postalCode,
      addressSource: 'YEABE',
      yeabeAddressId: a.yeabeAddressId,
    }));
    setNotice(
      a.countryCode && a.countryCode !== form.countryCode
        ? `Address imported from Yeabe — note it is in ${a.countryCode}, not the country of registration.`
        : 'Address imported from Yeabe.',
    );
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTypeTouched(true);
    setNotice(null);
    setSaveError(null);
    if (typeMissing) {
      setSaveError('Company type is required — it decides your rating track and document list.');
      return;
    }
    const fundingErrors = validateFunding(form);
    setFieldErrors(fundingErrors);
    const firstInvalid = Object.keys(fundingErrors)[0];
    if (firstInvalid) {
      setSaveError('Fix the highlighted funding fields before saving.');
      document.getElementById(`cp-${firstInvalid}`)?.focus();
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      addressSource: form.addressSource || (form.addressLine ? 'MANUAL' : null),
      foundedYear: form.foundedYear || null,
      employeeCount: form.employeeCount || null,
      fundingTenureMonths: form.fundingTenureMonths || null,
    };
    try {
      if (company) {
        await api.put('/customer/company', payload);
        if (logoFile) {
          const body = new FormData();
          body.append('file', logoFile);
          await api.post(`/customer/companies/${company.id}/logo`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else if (removeLogo) {
          await api.delete(`/customer/companies/${company.id}/logo`);
        }
        setLogoFile(null);
        setRemoveLogo(false);
        await Promise.all([overview.reload(true), refresh()]);
        setNotice('Company profile saved.');
      } else {
        const { data } = await api.post('/customer/companies', payload);
        if (logoFile) {
          const body = new FormData();
          body.append('file', logoFile);
          try {
            await api.post(`/customer/companies/${data.company.id}/logo`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch {
            await refresh();
            select(data.company.id);
            navigate('/company');
            setSaveError('Company created, but the logo could not be uploaded. Add it again from your profile.');
            return;
          }
        }
        await refresh();
        select(data.company.id);
        navigate('/documents');
      }
    } catch (err) {
      setSaveError(errorMessage(err, 'Could not save company profile.'));
    } finally {
      setSaving(false);
    }
  };

  const countryOptions = useMemo<SelectOption[]>(() => {
    const list = countriesApi.data?.countries || overview.data?.countries || [{ code: 'IN', name: 'India' }];
    return list.map((c) => ({ value: c.code, label: c.name, meta: c.code, leading: <span className="text-base leading-none">{flagEmoji(c.code)}</span> }));
  }, [countriesApi.data, overview.data]);

  const typeOptions = useMemo<SelectOption[]>(() => {
    const trackLabel = (code: RatingTrack) => tracks.find((t) => t.code === code)?.label || code;
    return types.map((t) => ({ value: t.code, label: t.label, description: t.hint, meta: trackLabel(t.track), group: `${trackLabel(t.track)} track` }));
  }, [types, tracks]);

  if (overview.loading) return <Loading />;
  if (overview.error) return <Notice tone="rose">{overview.error}</Notice>;

  const status = company ? COMPANY_STATUS[company.status] : null;
  const storedHint =
    company && company.fundingAmountValue != null && company.fundingAmount && form.fundingAmount === company.fundingAmount
      ? `Stored as ${company.fundingAmountValue.toLocaleString('en-US')}${company.fundingCurrency ? ` ${company.fundingCurrency}` : ''}`
      : null;

  return (
    <div>
      <ModuleHero
        eyebrow={isNewMode ? 'New company' : 'Register'}
        title={company ? 'Company profile' : isNewMode ? 'Add another company' : 'Register your company'}
        description={
          isNewMode
            ? 'Each company (or each bank / facility for the same company) gets its own workspace, document pack and rating.'
            : 'These details feed the AI verification and credit rating. Keep the legal name and registration number exactly as they appear on your documents.'
        }
        actions={
          <>
            {source && <StatusPill label={`Prefilled from ${source.logicalName || source.name}`} tone="blue" />}
            {status && <StatusPill label={status.label} tone={status.tone} />}
          </>
        }
      />

      <form onSubmit={onSubmit} className="mt-6 grid gap-5 lg:grid-cols-[1.35fr_0.65fr]" noValidate>
        <div className="space-y-5">
          <Card title="Legal entity" subtitle="Your entity type decides which rating methodology and document pack apply.">
            <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-[#fce7f3] bg-[#fffafc] p-4">
              {logoPreview && !removeLogo ? (
                <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
                  <img src={logoPreview} alt="Selected company logo preview" className="h-full w-full object-contain" />
                </span>
              ) : company?.hasLogo && !removeLogo ? (
                <CompanyLogo companyId={company.id} name={form.logicalName || form.name || company.logicalName || company.name} hasLogo className="h-16 w-16 rounded-xl bg-white" />
              ) : (
                <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#fdf2f8] text-xl font-black text-[#d6006e]">{(form.logicalName || form.name || '?').slice(0, 1).toUpperCase()}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-[#6b0030]">Company logo</p>
                <p className="mt-0.5 text-xs text-[#b0708f]">PNG, JPEG, or WebP · up to 5 MB. Shown across your Micus workspace and to your Micus team.</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-lg px-3 py-1.5 text-xs font-bold text-[#d6006e]" style={{ border: '1px solid #f9c0d8' }}>
                    {logoFile ? 'Choose another' : company?.hasLogo && !removeLogo ? 'Replace logo' : 'Upload logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        if (file && file.size > 5 * 1024 * 1024) {
                          setSaveError('Logo must be 5 MB or smaller.');
                          e.target.value = '';
                          return;
                        }
                        setLogoFile(file);
                        setRemoveLogo(false);
                        setSaveError(null);
                      }}
                    />
                  </label>
                  {(logoFile || (company?.hasLogo && !removeLogo)) && (
                    <button type="button" className="px-2 py-1.5 text-xs font-semibold text-[#9a6b82] hover:text-[#be123c]" onClick={() => { if (logoFile) { setLogoFile(null); setRemoveLogo(false); } else setRemoveLogo(true); }}>
                      Remove
                    </button>
                  )}
                  {logoFile && <span className="max-w-full truncate text-xs text-[#9a6b82]">{logoFile.name}</span>}
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={LABEL}>Country of registration *</label>
                <Select
                  value={form.countryCode}
                  onChange={(v) => setValue('countryCode', v)}
                  options={countryOptions}
                  disabled={locked}
                  searchable
                  searchPlaceholder="Search countries…"
                  aria-label="Country of registration"
                />
                {locked && <p className="mt-1 text-xs text-[#c084a0]">Locked after documents were submitted.</p>}
              </div>
              <div>
                <label className={LABEL}>Company type *</label>
                <Select
                  value={form.companyType}
                  onChange={(v) => {
                    setTypeTouched(true);
                    setValue('companyType', v);
                  }}
                  options={typeOptions}
                  placeholder="Select legal entity type"
                  disabled={locked}
                  searchable={false}
                  invalid={typeTouched && typeMissing}
                  aria-label="Company type"
                />
                {typeTouched && typeMissing && <p className="mt-1 text-xs font-semibold text-[#e11d48]">Company type is required.</p>}
              </div>
            </div>

            {track ? (
              <div className="mt-4 rounded-2xl p-4" style={{ background: '#fff5f8', border: '1px solid #fce7f3' }}>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill label={`${track.label} rating track`} tone={TRACK_TONE[track.code]} />
                  <span className="text-xs font-semibold text-[#6b0030]">{selectedType?.label}</span>
                </div>
                <p className="mt-2 text-sm text-[#6b0030]">{track.summary}</p>
                <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                  {track.methodology.map((m) => (
                    <li key={m} className="flex items-start gap-1.5 text-xs text-[#b0708f]">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#d6006e]" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="mt-3 text-xs text-[#c084a0]">
                Pick the entity type from your registration certificate — corporates, partnerships and proprietorships are rated on different methodologies.
              </p>
            )}

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="cp-logicalName" className={LABEL}>Micus company name *</label>
                <input id="cp-logicalName"
                  required
                  minLength={2}
                  maxLength={80}
                  pattern="[A-Za-z0-9][A-Za-z0-9 ._-]{1,79}"
                  className={FIELD}
                  style={FIELD_STYLE}
                  value={form.logicalName}
                  onChange={set('logicalName')}
                  placeholder="e.g. Acme Working Capital"
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-[#9a6b82]">
                  Unique name shown across Micus instead of your legal company name. Letters, numbers, spaces, dots, hyphens or underscores.
                </p>
              </div>
              <div>
                <label htmlFor="cp-name" className={LABEL}>Company / brand name *</label>
                <input id="cp-name" required minLength={2} className={FIELD} style={FIELD_STYLE} value={form.name} onChange={set('name')} />
                <p className="mt-1 text-xs text-[#9a6b82]">Your real operating or brand name — kept private for rating, documents and invoices.</p>
              </div>
              <div>
                <label htmlFor="cp-legalName" className={LABEL}>Registered legal name</label>
                <input id="cp-legalName" className={FIELD} style={FIELD_STYLE} value={form.legalName} onChange={set('legalName')} placeholder="As on incorporation certificate" />
              </div>
              <div>
                <label htmlFor="cp-registrationNumber" className={LABEL}>Registration number</label>
                <input id="cp-registrationNumber"
                  className={FIELD}
                  style={FIELD_STYLE}
                  value={form.registrationNumber}
                  onChange={set('registrationNumber')}
                  placeholder={REG_HINT[form.countryCode] || 'Company registration number'}
                />
              </div>
              <div>
                <label className={LABEL}>Sector</label>
                <Select value={form.sector} onChange={(v) => setValue('sector', v)} options={SECTORS} placeholder="Select sector" searchable aria-label="Sector" />
              </div>
              <div>
                <label htmlFor="cp-foundedYear" className={LABEL}>Founded year</label>
                <input id="cp-foundedYear" type="number" min={1800} max={2100} className={FIELD} style={FIELD_STYLE} value={form.foundedYear} onChange={set('foundedYear')} />
              </div>
              <div>
                <label htmlFor="cp-employeeCount" className={LABEL}>Employees</label>
                <input id="cp-employeeCount" type="number" min={0} className={FIELD} style={FIELD_STYLE} value={form.employeeCount} onChange={set('employeeCount')} />
              </div>
            </div>
          </Card>

          <Card title="Workspace" subtitle="Brokers and companies applying to several banks can keep a separate workspace per lender.">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cp-workspaceLabel" className={LABEL}>Workspace label</label>
                <input id="cp-workspaceLabel" className={FIELD} style={FIELD_STYLE} value={form.workspaceLabel} onChange={set('workspaceLabel')} placeholder="e.g. Working capital – HDFC" />
              </div>
              <div>
                <label htmlFor="cp-targetLender" className={LABEL}>Target bank / lender</label>
                <input id="cp-targetLender" className={FIELD} style={FIELD_STYLE} value={form.targetLender} onChange={set('targetLender')} placeholder="Leave blank to reach all partners" />
              </div>
            </div>
          </Card>

          <Card title="Contact & registered address">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cp-email" className={LABEL}>Company email</label>
                <input id="cp-email" type="email" className={FIELD} style={FIELD_STYLE} value={form.email} onChange={set('email')} placeholder="Defaults to your sign-in email" />
              </div>
              <div>
                <label htmlFor="cp-phone" className={LABEL}>Phone</label>
                <input id="cp-phone" className={FIELD} style={FIELD_STYLE} value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cp-website" className={LABEL}>Website</label>
                <input id="cp-website" className={FIELD} style={FIELD_STYLE} value={form.website} onChange={set('website')} placeholder="https://" />
              </div>
            </div>

            <div className="mt-4">
              <YeabeAddressPicker
                email={form.email}
                phone={form.phone}
                countryCode={form.countryCode}
                linked={form.addressSource === 'YEABE'}
                onPick={onPickAddress}
              />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="cp-addressLine" className={LABEL}>Address line 1</label>
                <input id="cp-addressLine" className={FIELD} style={FIELD_STYLE} value={form.addressLine} onChange={set('addressLine')} placeholder="Building, street" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cp-addressLine2" className={LABEL}>Address line 2</label>
                <input id="cp-addressLine2" className={FIELD} style={FIELD_STYLE} value={form.addressLine2} onChange={set('addressLine2')} placeholder="Area, landmark (optional)" />
              </div>
              <div>
                <label htmlFor="cp-city" className={LABEL}>City</label>
                <input id="cp-city" className={FIELD} style={FIELD_STYLE} value={form.city} onChange={set('city')} />
              </div>
              <div>
                <label htmlFor="cp-stateName" className={LABEL}>State / region</label>
                <input id="cp-stateName" className={FIELD} style={FIELD_STYLE} value={form.stateName} onChange={set('stateName')} />
              </div>
              <div>
                <label htmlFor="cp-postalCode" className={LABEL}>Postal code</label>
                <input id="cp-postalCode" className={FIELD} style={FIELD_STYLE} value={form.postalCode} onChange={set('postalCode')} />
              </div>
              <div className="flex items-end">
                <StatusPill
                  label={form.addressSource === 'YEABE' ? 'Source: Yeabe' : form.addressLine ? 'Source: manual' : 'No address yet'}
                  tone={form.addressSource === 'YEABE' ? 'green' : 'slate'}
                />
              </div>
            </div>
          </Card>

          <Card title="Business & funding" subtitle="Used in your rating narrative and the brief we send to funding partners.">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="cp-description" className={LABEL}>What the company does</label>
                <textarea id="cp-description" rows={3} className={`${FIELD} resize-y`} style={FIELD_STYLE} value={form.description} onChange={set('description')} />
              </div>
              <div>
                <label htmlFor="cp-annualRevenue" className={LABEL}>Annual revenue</label>
                <input id="cp-annualRevenue" className={FIELD} style={FIELD_STYLE} value={form.annualRevenue} onChange={set('annualRevenue')} placeholder="e.g. INR 12 Cr / USD 2.4M" />
              </div>
              <div>
                <label htmlFor="cp-fundingAmount" className={LABEL}>Funding ask</label>
                <input id="cp-fundingAmount" className={FIELD} style={FIELD_STYLE} value={form.fundingAmount} onChange={set('fundingAmount')} placeholder="e.g. USD 1.5M debt" {...fieldA11y('fundingAmount')} />
                {fieldError('fundingAmount')}
                {storedHint && <p className="mt-1 text-xs font-semibold text-[#047857]">{storedHint}</p>}
              </div>
              <div>
                <label htmlFor="cp-fundingCurrency" className={LABEL}>Funding currency</label>
                <input id="cp-fundingCurrency" className={FIELD} style={FIELD_STYLE} value={form.fundingCurrency} onChange={set('fundingCurrency')} placeholder="e.g. INR, USD" maxLength={3} {...fieldA11y('fundingCurrency')} />
                {fieldError('fundingCurrency')}
                <p className="mt-1 text-xs text-[#9a6b82]">3-letter code. Required whenever a funding amount is entered.</p>
              </div>
              <div>
                <label className={LABEL}>Funding instrument</label>
                <Select
                  value={form.fundingInstrument}
                  onChange={(v) => setValue('fundingInstrument', v)}
                  options={[
                    { value: 'DEBT', label: 'Debt' },
                    { value: 'EQUITY', label: 'Equity' },
                    { value: 'HYBRID', label: 'Hybrid' },
                    { value: 'OTHER', label: 'Other' },
                  ]}
                  placeholder="Select instrument"
                  aria-label="Funding instrument"
                  invalid={Boolean(fieldErrors.fundingInstrument)}
                />
                {fieldError('fundingInstrument')}
                <p className="mt-1 text-xs leading-relaxed text-[#9a6b82]">
                  A debt ask needs tenure, a repayment plan and a currency.{' '}
                  <GlossaryHint term="Why?" meaning="Lenders cannot assess a debt facility without knowing how long it runs, how it will be repaid and in which currency. Equity and other instruments do not need these three fields." />
                </p>
              </div>
              <div>
                <label htmlFor="cp-fundingTenureMonths" className={LABEL}>Tenure (months)</label>
                <input id="cp-fundingTenureMonths" type="number" min={1} max={600} className={FIELD} style={FIELD_STYLE} value={form.fundingTenureMonths} onChange={set('fundingTenureMonths')} {...fieldA11y('fundingTenureMonths')} />
                {fieldError('fundingTenureMonths')}
                <p className="mt-1 text-xs text-[#9a6b82]">Length of the facility in months. Required for debt.</p>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cp-fundingPurpose" className={LABEL}>Use of funds</label>
                <input id="cp-fundingPurpose" className={FIELD} style={FIELD_STYLE} value={form.fundingPurpose} onChange={set('fundingPurpose')} placeholder="Working capital, expansion…" />
              </div>
              <div>
                <label htmlFor="cp-fundingRepaymentPlan" className={LABEL}>Repayment plan</label>
                <textarea id="cp-fundingRepaymentPlan" rows={2} className={`${FIELD} resize-y`} style={FIELD_STYLE} value={form.fundingRepaymentPlan} onChange={set('fundingRepaymentPlan')} placeholder="Expected repayment source and schedule" {...fieldA11y('fundingRepaymentPlan')} />
                {fieldError('fundingRepaymentPlan')}
                <p className="mt-1 text-xs text-[#9a6b82]">Where repayments will come from and how often. Required for debt.</p>
              </div>
              <div>
                <label htmlFor="cp-fundingSecurity" className={LABEL}>Security / collateral</label>
                <textarea id="cp-fundingSecurity" rows={2} className={`${FIELD} resize-y`} style={FIELD_STYLE} value={form.fundingSecurity} onChange={set('fundingSecurity')} placeholder="Assets or guarantees offered, if any" />
              </div>
            </div>
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          {company && <PersonCard person={company.assignedAdmin} label="Your Micus analyst" empty="Assigned when you submit documents" />}
          {saveError && <Notice tone="rose">{saveError}</Notice>}
          {notice && <Notice>{notice}</Notice>}
          <button type="submit" disabled={saving} className={`${BTN_PRIMARY} w-full py-3`} style={BTN_PRIMARY_STYLE}>
            {saving && <Spinner />}
            {company ? 'Save profile' : 'Create company & continue to documents →'}
          </button>
          {isNewMode && (
            <Link to="/" className="block text-center text-xs font-bold text-[#c084a0] hover:text-[#d6006e]">
              Cancel
            </Link>
          )}
        </aside>
      </form>
    </div>
  );
}
